import React, { useState, useRef, useEffect, FC } from 'react';
import { projects as projectsData } from '../../utils/data';
import useSound from '../../hooks/useSound';
import TerminalTyping from '../TerminalTyping';
import { FaGithub, FaExternalLinkAlt, FaCode } from 'react-icons/fa';
import './Projects.css';

// Define your CV projects by ID to only show these specific ones
const CV_PROJECT_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 17]; // Adjust these IDs based on your actual CV projects

const Projects: FC = () => {
  const { playClick, playHover } = useSound();
  const [filteredSkill, setFilteredSkill] = useState<string | null>(null);
  // Always set appearingCards to true to keep projects visible
  const [appearingCards, setAppearingCards] = useState<boolean>(true);
  const projectsRef = useRef<HTMLElement | null>(null);

  // Filter projects by selected skill and CV projects
  const cvProjects = projectsData.filter(project => CV_PROJECT_IDS.includes(project.id));

  // Listen for project highlight events from skills section
  useEffect(() => {
    const handleProjectHighlight = (event: CustomEvent) => {
      const skillName = event.detail.skillName;
      setFilteredSkill(skillName || null);
      
      // Play a sound when filtering projects
      playHover();
      
      // Add animation for the filtered notification
      setTimeout(() => {
        const notification = document.querySelector('.filter-notification');
        if (notification) {
          notification.classList.add('filter-appear');
        }
      }, 100);
    };
    
    window.addEventListener('highlightProject', handleProjectHighlight as EventListener);
    return () => {
      window.removeEventListener('highlightProject', handleProjectHighlight as EventListener);
    };
  }, [playHover]);

  // Filter projects by selected skill from CV projects only
  const filteredProjects = filteredSkill
    ? cvProjects.filter(project =>
        project.technologies.some(tech => tech.toLowerCase() === filteredSkill.toLowerCase())
      )
    : cvProjects;

  const handleClearFilter = () => {
    playClick();
    
    // Add exit animation for the filter notification
    const notification = document.querySelector('.filter-notification');
    if (notification) {
      notification.classList.remove('filter-appear');
      notification.classList.add('filter-disappear');
      
      setTimeout(() => {
        setFilteredSkill(null);
      }, 300);
    } else {
      setFilteredSkill(null);
    }
  };

  // Generate terminal text for each project
  const getTerminalText = (projectTitle: string, tech: string[]) => {
    return [`$ loading ${projectTitle.toLowerCase().replace(/\s+/g, '-')}`, 
            `$ Tech stack: ${tech.slice(0, 3).join(', ')}...`];
  };

  return (
    <section className="projects-section" id="projects" ref={projectsRef as React.RefObject<HTMLElement>}>
      <div className="section-background-effects">
        <div className="grid-lines"></div>
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="floating-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDuration: `${Math.random() * 5 + 10}s`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          ></div>
        ))}
      </div>
      
      <div className="projects-content">
        <h2 className="section-title glitch-appear">
          <span data-text="Projects" className="glitch-text">Projects</span>
        </h2>
        
        {filteredSkill && (
          <div className={`filter-notification ${appearingCards ? 'filter-appear' : ''}`}>
            <span>Showing projects using </span>
            <span className="filter-tech">{filteredSkill}</span>
            <button className="clear-filter" onClick={handleClearFilter}>×</button>
          </div>
        )}
        
        <div className="projects-grid">
          {filteredProjects.length > 0 ? (
            filteredProjects.map((project, index) => (
              <div 
                className="project-card appear" 
                style={{ 
                  animationDelay: `${index * 0.1}s`,
                  transitionDelay: `${index * 0.05}s`
                }}
                key={project.id}
                onMouseEnter={() => playHover()}
              >
                <div className="project-corner-effect top-left"></div>
                <div className="project-corner-effect top-right"></div>
                <div className="project-corner-effect bottom-left"></div>
                <div className="project-corner-effect bottom-right"></div>
                
                <div className="terminal-pulse"></div>
                
                <div className="project-code">
                  <FaCode />
                </div>
                
                <div className="project-header">
                  <h3 className="project-title">{project.title}</h3>
                  <div className="project-tech-list">
                    {project.technologies.slice(0, 5).map((tech, idx) => (
                      <span 
                        key={idx} 
                        className={`project-tech ${filteredSkill && tech.toLowerCase() === filteredSkill.toLowerCase() ? 'highlighted' : ''}`}
                      >
                        {tech}
                      </span>
                    ))}
                    {project.technologies.length > 5 && 
                      <span className="project-tech">+{project.technologies.length - 5}</span>
                    }
                  </div>
                </div>
                
                <div className="project-details">
                  <div className="project-description">
                    {project.description}
                  </div>
                  
                  <div className="project-links">
                    {project.github && (
                      <a 
                        href={project.github} 
                        className="project-link"
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={() => playClick()}
                      >
                        <FaGithub className="project-link-icon" />
                        <span>GitHub</span>
                      </a>
                    )}
                    
                    {project.demo && (
                      <a 
                        href={project.demo} 
                        className="project-link"
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={() => playClick()}
                      >
                        <FaExternalLinkAlt className="project-link-icon" />
                        <span>Live Demo</span>
                      </a>
                    )}
                  </div>
                </div>
                
                <div className="project-terminal">
                  <TerminalTyping
                    text={getTerminalText(project.title, project.technologies)}
                    typingSpeed={30}
                    loop={true}
                  />
                </div>
              </div>
            ))
          ) : (
            <div className="no-projects-message">
              <div className="error-icon">!</div>
              <h3>No projects found</h3>
              <p>No projects matching the selected skill were found.</p>
              <button className="reset-button" onClick={handleClearFilter}>
                Clear filter
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Projects;